import React from 'react';
import '../../styles/help.css'; // Reusing the same style file for consistency
import EnableScrollOnPage from '../../Integrations/EnableScroll'; // Import the scroll enabler

const TCPrivacyPolicyPage = () => {
  return (
    <div className="help-container">
      <EnableScrollOnPage />
      <h1>Privacy Policy for Train Chess</h1>
      <p><strong>Effective Date:</strong> January 22, 2025</p>
      <p><strong>Last Updated:</strong> February 9, 2026</p>

      <p>
        Welcome to <em>Train Chess</em>, a mobile chess training application provided by
        <strong> Train Chess</strong> (“we,” “us,” or “our”). This Privacy Policy explains
        how we collect, use, and protect your information when you use the Train Chess app.
      </p>

      <section className="policy-section">
        <h2>1. Information We Collect</h2>
        <p>When you use <em>Train Chess</em>, we may collect the following information to operate and improve the app:</p>
        <ul>
          <li>
            <strong>Account Information:</strong>
            <ul>
              <li>Email address, username, and profile-related information.</li>
              <li>Authentication credentials handled securely through Firebase Authentication.</li>
            </ul>
          </li>
          <li>
            <strong>Training &amp; Usage Data:</strong>
            <ul>
              <li>Chess training activity such as puzzles attempted, progress, ratings, streaks, and preferences.</li>
              <li>Interactions within the app used to personalize training and improve features.</li>
            </ul>
          </li>
          <li>
            <strong>Device &amp; Technical Information:</strong>
            <ul>
              <li>Device type, operating system, app version, and anonymous device identifiers.</li>
              <li>Crash logs and performance diagnostics.</li>
            </ul>
          </li>
          <li>
            <strong>Analytics &amp; Notification Data:</strong>
            <ul>
              <li>Anonymous usage analytics such as feature usage and interaction patterns to help improve the app.</li>
              <li>Push notification tokens used to deliver training reminders and important app notifications.</li>
            </ul>
          </li>
        </ul>
        <p>
          Train Chess does <strong>not</strong> support user-generated content such as public posts,
          comments, or media uploads.
        </p>
      </section>

      <section className="policy-section">
        <h2>2. How We Use Your Information</h2>
        <p>We use collected information to:</p>
        <ul>
          <li>Provide and improve chess training features and recommendations.</li>
          <li>Track training progress and personalize the learning experience.</li>
          <li>Maintain app security and prevent abuse or fraud.</li>
          <li>Diagnose bugs, improve performance, and develop new features.</li>
          <li>Respond to support requests and user inquiries.</li>
          <li>Send optional push notifications related to training reminders, progress, or important app updates.</li>
        </ul>
      </section>

      <section className="policy-section">
        <h2>3. Data Storage and Security</h2>
        <p>
          Train Chess uses <strong>Google Firebase</strong> and related cloud services to securely
          store and process data. We apply industry‑standard security practices, including
          encryption and access controls, to protect your information.
        </p>
        <p>
          If you choose to delete your account, your associated data will be permanently
          removed from our active systems within a reasonable timeframe, except where
          retention is required by law.
        </p>
        <p>
          Train Chess is a <strong>mobile‑only application</strong>. All data is collected and
          accessed through the iOS and Android mobile apps.
        </p>
      </section>

      <section className="policy-section">
        <h2>4. Sharing of Information</h2>
        <p>We do <strong>not</strong> sell your personal information.</p>
        <p>We may share limited data only with:</p>
        <ul>
          <li>
            <strong>Service Providers:</strong>
            <ul>
              <li>
                Trusted infrastructure, analytics, and notification providers
                (such as Firebase) strictly for operating and improving the app.
              </li>
            </ul>
          </li>
          <li>
            <strong>Legal Requirements:</strong>
            <ul>
              <li>
                When required to comply with applicable laws, regulations, or legal processes.
              </li>
            </ul>
          </li>
        </ul>
      </section>

      <section className="policy-section">
        <h2>5. User Rights and Choices</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access and update your account information within the app.</li>
          <li>Request deletion of your account and associated data.</li>
          <li>Contact us with questions or concerns about your data or this policy.</li>
        </ul>
        <p>
          Requests can be made through the app or by contacting us at
          <a href="mailto:contact@trainchess.net"> contact@trainchess.net</a>.
        </p>
      </section>

      <section className="policy-section">
        <h2>6. Children's Privacy</h2>
        <p>
          Train Chess is <strong>not intended for children under the age of 13</strong>.
          We do not knowingly collect personal information from children under 13.
        </p>
        <p>
          If we learn that such data has been collected, we will take steps to delete
          it promptly. If you believe this has occurred, please contact us.
        </p>
      </section>

      <section className="policy-section">
        <h2>7. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Any material changes
          will be communicated within the app or through other appropriate means.
          Continued use of the app after updates constitutes acceptance of the
          revised policy.
        </p>
      </section>

      <section className="policy-section">
        <h2>8. Contact Us</h2>
        <p>If you have any questions or concerns about this Privacy Policy or your data, please contact us:</p>
        <p>
          <strong>Email:</strong>
          <a href="mailto:contact@trainchess.net"> contact@trainchess.net</a>
        </p>
      </section>
    </div>
  );
};

export default TCPrivacyPolicyPage;
